import { useDroppable } from "@dnd-kit/core";
import { useSidebarDndContext } from "@/contexts/SidebarDndProvider";
import { cn } from "@/lib/utils";
import PropTypes from "prop-types";

export function DroppableWorkspace({
  workspaceId,
  children,
  className,
  style,
  disabled = false,
}) {
  // Get drag source to disable cross-area drops
  // Cross-area drag between sidebar and project page is not supported
  const dndContext = useSidebarDndContext();
  const dragSource = dndContext?.dragSource;

  // Disable drop indicators entirely - cross-area drag is not supported
  // Reordering within project page uses SortableContext, not this droppable
  const effectivelyDisabled = disabled || dragSource === "sidebar";

  const { isOver, setNodeRef } = useDroppable({
    id: `workspace-drop-${workspaceId}`,
    data: {
      type: "workspace",
      workspaceId,
    },
    disabled: effectivelyDisabled,
  });

  // Don't show drop indicators for cross-area drags
  const showDropIndicator = isOver && !effectivelyDisabled;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "workspace-drop-zone transition-all duration-200",
        showDropIndicator && "drag-over",
        className
      )}
      style={style}
    >
      {children}

      {/* Drop indicator overlay - only for same-area drops */}
      {showDropIndicator && (
        <div className="absolute inset-0 pointer-events-none rounded-lg border-2 border-dashed border-purple-500/50 bg-purple-500/10 flex items-center justify-center z-10">
          <span className="text-sm text-purple-600 dark:text-purple-400 font-medium bg-white/90 dark:bg-zinc-900/90 px-3 py-1 rounded-full">
            Drop to move here
          </span>
        </div>
      )}

      <style>{`
        .workspace-drop-zone {
          position: relative;
        }

        .workspace-drop-zone.drag-over {
          background-color: rgba(147, 51, 234, 0.05);
        }

        .dark .workspace-drop-zone.drag-over {
          background-color: rgba(147, 51, 234, 0.1);
        }
      `}</style>
    </div>
  );
}

DroppableWorkspace.propTypes = {
  workspaceId: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  disabled: PropTypes.bool,
};

export default DroppableWorkspace;
